import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Volume2,
  Pause,
  Video,
  Download,
  Loader2,
  MoreVertical,
  Trash2,
  Search,
  ListFilter,
  Upload,
  ChevronRight,
  Music
} from "lucide-react";

import {
  createProject,
  deleteProject,
  uploadAudio,
  createSession,
  fetchProjects,
  type ProjectSummary,
  type ProjectWorkflowStatus,
  getProjectFinalVideoUrl,
  fetchProjectTranscriptions,
  fetchProjectKnowledgeGraph
} from "@/api/client";
import loadingBlueIconUrl from "@/assets/svg/loading-blue.svg?url";
import { useSessionStore } from "@/hooks/useSessionStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { ProjectCard } from "@/components/project-selection/ProjectCard";
import { CreateProjectModal } from "@/components/project-selection/CreateProjectModal";
import { DownloadProjectArchiveDialog } from "@/components/project-selection/DownloadProjectArchiveDialog";
import { useProjectFiltering } from "@/hooks/useProjectFiltering";

type SessionBootstrap = {
  steps?: Record<string, unknown>;
  scenarios?: unknown[];
  selected_scenario?: unknown;
  scenario_audio?: { path?: string | null } | null;
  current_step?: string;
  session_id: string;
};

const deriveProgressFromSession = (session: SessionBootstrap | null, requireFreshEdit: boolean) => {
  const steps = (session?.steps ?? {}) as Record<string, unknown>;
  const storedScenarios = Array.isArray(session?.scenarios) ? session.scenarios : [];
  const selectedScenario = session?.selected_scenario;
  const audioMeta = session?.scenario_audio;
  const audioSources = (steps as Record<string, unknown>).audio_sources;
  const transcriptionStepDone = Boolean((steps as Record<string, unknown>).transcription_review);
  const progressedBeyondTranscription =
    Boolean(
      (steps as Record<string, unknown>).scenario_review ||
        (steps as Record<string, unknown>).scenario_edit ||
        (steps as Record<string, unknown>).final_validation
    ) ||
    Boolean(selectedScenario) ||
    Boolean((audioMeta as { path?: string } | null)?.path);
  return {
    audioReady: Boolean(audioSources || (audioMeta as { path?: string } | null)?.path),
    transcriptionsReviewed: transcriptionStepDone || progressedBeyondTranscription,
    scenariosReady: Boolean(storedScenarios.length > 0 || selectedScenario),
    scenarioChosen: Boolean(selectedScenario),
    scenarioEdited: requireFreshEdit ? false : Boolean((audioMeta as { path?: string } | null)?.path)
  };
};

const formatAddedLong = (value?: string) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
};

const WORKFLOW_LABELS: Record<ProjectWorkflowStatus, string> = {
  termine: "Terminé",
  en_cours: "En cours",
  brouillon: "Brouillon"
};

function workflowBadgeVariant(status: ProjectWorkflowStatus | undefined): "success" | "warning" | "muted" {
  if (status === "termine") return "success";
  if (status === "en_cours") return "warning";
  return "muted";
}

/** Couleurs brand / system (design system) — bandeau haut de carte, stable par nom de projet */
const CARD_TOP_ACCENT_HEX = [
  "#007AFF",
  "#FFA202",
  "#C8009C",
  "#623DC7",
  "#04A404",
  "#92B2FF",
  "#45556C"
] as const;

const GRAPH_WAIT_POLL_MS = 3000;
const GRAPH_WAIT_TIMEOUT_MS = 10 * 60 * 1000;

function stringHash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function cardTopAccentHex(projectName: string): string {
  return CARD_TOP_ACCENT_HEX[stringHash(projectName) % CARD_TOP_ACCENT_HEX.length];
}


export function ProjectSelectionView() {
  const { data, refetch, isLoading } = useQuery({ queryKey: ["projects"], queryFn: fetchProjects });
  const {
    setProjectName,
    setSessionId,
    setCurrentStep,
    setScenarioTarget,
    lastProjectName,
    setLastProjectName,
    resetProgress,
    setProgress
  } = useSessionStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [scenarioTargetDraft, setScenarioTargetDraft] = useState(3);
  const [error, setError] = useState<string | null>(null);
  const [previewProject, setPreviewProject] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [videoProject, setVideoProject] = useState<string | null>(null);
  const [menuProject, setMenuProject] = useState<string | null>(null);
  const projectMenuRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [createArchiveOpen, setCreateArchiveOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const filterMenuRef = useRef<HTMLDivElement | null>(null);

  const [downloadProject, setDownloadProject] = useState<ProjectSummary | null>(null);
  const [waitingProjectName, setWaitingProjectName] = useState<string | null>(null);
  const [waitingTotalFiles, setWaitingTotalFiles] = useState(0);
  // Phase 2 (graphe) : nombre de transcriptions réussies à couvrir + instant de départ.
  const [graphWait, setGraphWait] = useState<{ done: number; startedAt: number } | null>(null);

  const transcriptionWaitQuery = useQuery({
    queryKey: ["transcription-wait-creation", waitingProjectName],
    queryFn: () => fetchProjectTranscriptions(waitingProjectName!),
    enabled: Boolean(waitingProjectName),
    refetchInterval: (query) => {
      if (!waitingProjectName) return false;
      const done = query.state.data?.transcriptions.length ?? 0;
      const failed = query.state.data?.failedFiles.length ?? 0;
      return waitingTotalFiles > 0 && done + failed < waitingTotalFiles ? 3000 : false;
    },
  });

  const graphWaitQuery = useQuery({
    queryKey: ["project-knowledge-graph", waitingProjectName],
    queryFn: () => fetchProjectKnowledgeGraph(waitingProjectName!),
    enabled: Boolean(waitingProjectName) && graphWait !== null,
    refetchInterval: GRAPH_WAIT_POLL_MS,
  });

  const lastTerminalBuildRef = useRef<string | null>(null);

  const finishWaiting = (project: string) => {
    lastTerminalBuildRef.current = null;
    queryClient.invalidateQueries({ queryKey: ["project-knowledge-graph", project] });
    setWaitingProjectName(null);
    setWaitingTotalFiles(0);
    setGraphWait(null);
    navigate("/step/project_details");
  };

  // Phase 1 -> 2 : transcriptions terminées.
  useEffect(() => {
    if (!waitingProjectName || graphWait) return;
    const done = transcriptionWaitQuery.data?.transcriptions.length ?? 0;
    const failed = transcriptionWaitQuery.data?.failedFiles.length ?? 0;
    if (waitingTotalFiles > 0 && done + failed >= waitingTotalFiles) {
      if (done === 0) finishWaiting(waitingProjectName);
      else setGraphWait({ done, startedAt: Date.now() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcriptionWaitQuery.data, waitingProjectName, waitingTotalFiles, graphWait]);

  // Phase 2 : graphe construit (ou en échec / vide) avec au moins `done` sources lues.
  useEffect(() => {
    if (!waitingProjectName || !graphWait) return;
    const build = graphWaitQuery.data?.build;
    if (build && build.status !== "building" && build.sources_count >= graphWait.done) {
      finishWaiting(waitingProjectName);
      return;
    }
    // Sortie de secours : failed/empty relu à l'identique sur 2 sondages consécutifs
    // (ex. extraction des sources en erreur, sources_count resté à 0).
    if (build && (build.status === "failed" || build.status === "empty")) {
      const sig = `${build.status}|${build.updated_at}`;
      if (lastTerminalBuildRef.current === sig) finishWaiting(waitingProjectName);
      else lastTerminalBuildRef.current = sig;
    } else {
      lastTerminalBuildRef.current = null;
    }
    // dataUpdatedAt change à chaque sondage réussi, même si les données sont identiques.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphWaitQuery.data, graphWaitQuery.dataUpdatedAt, waitingProjectName, graphWait]);

  // Garde-fou : on ne bloque jamais l'utilisateur plus de 10 min sur le graphe.
  useEffect(() => {
    if (!waitingProjectName || !graphWait) return;
    const remaining = Math.max(0, GRAPH_WAIT_TIMEOUT_MS - (Date.now() - graphWait.startedAt));
    const timer = window.setTimeout(() => finishWaiting(waitingProjectName), remaining);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingProjectName, graphWait]);

  const projects = data ?? [];
  const {
    archiveSearch,
    setArchiveSearch,
    sortBy,
    setSortBy,
    sortAsc,
    setSortAsc,
    filteredProjects,
  } = useProjectFiltering(projects);

  useEffect(() => {
    if (!menuProject) return;
    const onPointerDown = (e: MouseEvent) => {
      const el = projectMenuRef.current;
      if (el && !el.contains(e.target as Node)) setMenuProject(null);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [menuProject]);

  useEffect(() => {
    if (!filterMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const el = filterMenuRef.current;
      if (el && !el.contains(e.target as Node)) setFilterMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [filterMenuOpen]);

  const togglePreview = (projectName: string) => {
    setPreviewProject((prev) => (prev === projectName ? null : projectName));
    setPreviewKey((k) => k + 1);
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error("Nom requis");
      const trimmed = name.trim();
      await createProject({ name: trimmed, description, scenario_target: scenarioTargetDraft });
      const totalUploaded = selectedFiles.length;
      if (totalUploaded > 0) {
        await Promise.all(selectedFiles.map((file) => uploadAudio(trimmed, file)));
      }
      const session = await createSession(trimmed, "project_selection", scenarioTargetDraft);
      setProjectName(trimmed);
      setLastProjectName(trimmed);
      setSessionId(session.session_id);
      setCurrentStep("project_details");
      setScenarioTarget(scenarioTargetDraft);
      resetProgress();
      setName("");
      setPreviewProject(null);
      setVideoProject(null);
      setCreateArchiveOpen(false);
      setSelectedFiles([]);
      refetch();
      if (totalUploaded > 0) {
        setWaitingProjectName(trimmed);
        setWaitingTotalFiles(totalUploaded);
      } else {
        navigate("/step/project_details");
      }
    },
    onError: (err) => setError((err as Error).message)
  });

  const handleCreate = (evt: FormEvent) => {
    evt.preventDefault();
    setError(null);
    createMutation.mutate();
  };

  const handleAddArchiveClick = () => setCreateArchiveOpen(true);

  const pickFiles = () => fileInputRef.current?.click();

  const [fileDurations, setFileDurations] = useState<Record<string, string>>({});
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const list = Array.from(e.dataTransfer.files);
      const audioFiles = list.filter(f => f.type.startsWith("audio/") || f.name.endsWith(".mp3") || f.name.endsWith(".wav") || f.name.endsWith(".m4a"));
      if (audioFiles.length > 0) {
        setSelectedFiles(audioFiles);
        // resolve durations
        audioFiles.forEach((file) => {
          const audio = new Audio();
          audio.src = URL.createObjectURL(file);
          audio.onloadedmetadata = () => {
            const minutes = Math.floor(audio.duration / 60);
            const seconds = Math.floor(audio.duration % 60);
            setFileDurations((prev) => ({ ...prev, [file.name]: `${minutes}:${seconds.toString().padStart(2, "0")}` }));
            URL.revokeObjectURL(audio.src);
          };
          audio.onerror = () => {
            setFileDurations((prev) => ({ ...prev, [file.name]: "0:00" }));
            URL.revokeObjectURL(audio.src);
          };
        });
      }
    }
  };

  const onFilesSelected = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(evt.target.files ?? []);
    setSelectedFiles(list);

    // Resolve durations
    list.forEach((file) => {
      const audio = new Audio();
      audio.src = URL.createObjectURL(file);
      audio.onloadedmetadata = () => {
        const minutes = Math.floor(audio.duration / 60);
        const seconds = Math.floor(audio.duration % 60);
        setFileDurations((prev) => ({ ...prev, [file.name]: `${minutes}:${seconds.toString().padStart(2, "0")}` }));
        URL.revokeObjectURL(audio.src);
      };
      audio.onerror = () => {
        setFileDurations((prev) => ({ ...prev, [file.name]: "0:00" }));
        URL.revokeObjectURL(audio.src);
      };
    });
  };

  const bootstrapProjectSession = async (project: ProjectSummary) => {
    resetProgress();
    const session = (await createSession(project.name, "project_selection", project.scenario_target)) as SessionBootstrap;
    setProgress(deriveProgressFromSession(session, Boolean(project.finalized_at)));
    setProjectName(project.name);
    setLastProjectName(project.name);
    setSessionId(session.session_id);
    let targetStep = session.current_step || "project_details";
    if (targetStep === "project_selection") {
      targetStep = "project_details";
    }
    setCurrentStep(targetStep);
    setScenarioTarget(project.scenario_target);
    setPreviewProject(null);
    setVideoProject(null);
    setMenuProject(null);
    return session;
  };

  const handleSelect = async (project: ProjectSummary) => {
    try {
      setError(null);
      await bootstrapProjectSession(project);
      navigate("/step/project_details");
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="-m-6 min-h-full bg-[#F4F4F4] px-6 py-6">
      <div className="flex w-full max-w-none flex-col gap-6">
        <div className="-mx-6 flex flex-col gap-3 px-4 sm:flex-row sm:items-center sm:justify-between sm:gap-10 sm:px-5">
          <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 sm:pl-4 sm:pr-6">
            <div className="relative min-w-0 flex-1 sm:max-w-[360px]">
              <Input
                type="search"
                value={archiveSearch}
                onChange={(e) => setArchiveSearch(e.target.value)}
                placeholder="Rechercher une archive…"
                aria-label="Rechercher une archive"
                className="h-10 rounded-full border-border bg-background pr-10 pl-4 text-sm shadow-sm"
              />
              <Search
                className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
            </div>
            <div className="relative shrink-0" ref={filterMenuRef}>
              <Button
                type="button"
                variant="outline"
                className="h-10 gap-2 rounded-full border-border bg-background px-4 font-medium shadow-sm sm:self-stretch"
                aria-label="Trier les archives"
                onClick={() => setFilterMenuOpen((v) => !v)}
              >
                Trier
                <ListFilter className="size-4 shrink-0" aria-hidden />
              </Button>
              {filterMenuOpen && (
                <div className="absolute left-0 top-full z-20 mt-2 w-[220px] overflow-hidden rounded-[8px] border border-[#E2E8F0] bg-background text-sm shadow-[0_2px_10px_rgba(0,0,0,0.10)]">
                  {(["alpha", "date"] as const).map((mode) => {
                    const active = sortBy === mode;
                    const label = mode === "alpha" ? "Alphabétique" : "Par ancienneté";
                    const dirLabel = mode === "alpha"
                      ? (sortAsc ? "A → Z" : "Z → A")
                      : (sortAsc ? "Plus ancien" : "Plus récent");
                    return (
                      <button
                        key={mode}
                        type="button"
                        className={`flex h-[52px] w-full items-center justify-between gap-2 px-4 py-2 text-left transition-colors hover:bg-muted ${active ? "font-semibold text-primary" : "text-foreground"}`}
                        onClick={() => {
                          if (active) {
                            setSortAsc((v) => !v);
                          } else {
                            setSortBy(mode);
                            setSortAsc(mode === "alpha");
                          }
                          setFilterMenuOpen(false);
                        }}
                      >
                        <span>{label}</span>
                        <span className={`text-xs ${active ? "text-primary" : "text-muted-foreground"}`}>{dirLabel}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <Button
            type="button"
            onClick={handleAddArchiveClick}
            className="h-[38px] w-fit shrink-0 gap-2 rounded-[12px] bg-primary px-4 text-primary-foreground hover:bg-primary/90"
            aria-label="Ajouter une archive"
          >
            <Plus className="size-4 shrink-0" />
            Ajouter une archive
          </Button>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement…
          </div>
        ) : projects.length > 0 ? (
          filteredProjects.length > 0 ? (
          <ul className="mt-20 grid grid-cols-1 gap-y-5 sm:grid-cols-[repeat(2,minmax(0,560px))] sm:justify-center sm:gap-x-5">
              {filteredProjects.map((project) => {
                const displayWf = project.workflow_status ?? "brouillon";
                return (
                  <ProjectCard
                    key={project.name}
                    project={project}
                    displayWf={displayWf}
                    menuOpen={menuProject === project.name}
                    previewProject={previewProject}
                    previewKey={previewKey}
                    menuRef={projectMenuRef}
                    onSelect={handleSelect}
                    onMenuToggle={(name) => setMenuProject((prev) => (prev === name ? null : name))}
                    onDownloadOpen={(proj) => {
                      setMenuProject(null);
                      setDownloadProject(proj);
                    }}
                    onDelete={async (proj) => {
                      setMenuProject(null);
                      try {
                        setError(null);
                        await deleteProject(proj.name);
                        if (lastProjectName === proj.name) setLastProjectName(undefined);
                        await refetch();
                      } catch (err) {
                        setError((err as Error).message);
                      }
                    }}
                  />
                );
              })}
          </ul>
          ) : (
            <p className="text-sm text-muted-foreground py-2">Aucun résultat pour cette recherche.</p>
          )
        ) : (
          <p className="text-sm text-muted-foreground py-2">Aucun projet enregistré pour le moment.</p>
        )}

        {/* <Card>Nouveau projet</Card> replaced by modal flow */}

        <Dialog open={Boolean(videoProject)} onOpenChange={(open) => !open && setVideoProject(null)}>
          <DialogContent className="max-w-3xl" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Diaporama — {videoProject}</DialogTitle>
            </DialogHeader>
            {videoProject && (
              <video
                key={videoProject}
                controls
                className="w-full max-h-[70vh] rounded-lg"
                src={getProjectFinalVideoUrl(videoProject)}
              />
            )}
          </DialogContent>
        </Dialog>
        <CreateProjectModal
          open={createArchiveOpen}
          onOpenChange={setCreateArchiveOpen}
          name={name}
          onNameChange={setName}
          selectedFiles={selectedFiles}
          fileDurations={fileDurations}
          dragActive={dragActive}
          isPending={createMutation.isPending}
          fileInputRef={fileInputRef}
          onSubmit={handleCreate}
          onDrag={handleDrag}
          onDrop={handleDrop}
          onPickFiles={pickFiles}
          onFilesSelected={onFilesSelected}
          onRemoveFile={(idx) => setSelectedFiles((prev) => prev.filter((_, i) => i !== idx))}
        />
        <DownloadProjectArchiveDialog
          project={downloadProject}
          onClose={() => setDownloadProject(null)}
          onError={(msg) => setError(msg)}
        />

        <Dialog open={Boolean(waitingProjectName)}>
          <DialogContent hideCloseButton className="max-w-md border border-blue-100 bg-white p-6 shadow-xl rounded-2xl" onInteractOutside={(e) => e.preventDefault()}>
            <style dangerouslySetInnerHTML={{__html: `
              @keyframes soundwave-pulse {
                0%, 100% { transform: scaleY(0.3); }
                50% { transform: scaleY(1); }
              }
              .soundwave-bar {
                transform-origin: bottom;
                animation: soundwave-pulse 1.2s ease-in-out infinite;
              }
            `}} />
            <div className="flex flex-col items-center text-center">
              <div className="flex items-end justify-center gap-1.5 h-12 mb-6">
                <span className="w-1.5 h-full bg-blue-500 rounded-full soundwave-bar" style={{ animationDelay: '0.1s' }} />
                <span className="w-1.5 h-full bg-blue-400 rounded-full soundwave-bar" style={{ animationDelay: '0.3s' }} />
                <span className="w-1.5 h-full bg-blue-600 rounded-full soundwave-bar" style={{ animationDelay: '0.5s' }} />
                <span className="w-1.5 h-full bg-blue-500 rounded-full soundwave-bar" style={{ animationDelay: '0.2s' }} />
                <span className="w-1.5 h-full bg-blue-400 rounded-full soundwave-bar" style={{ animationDelay: '0.4s' }} />
              </div>

              <h3 className="text-xl font-bold text-[#0F172B] tracking-tight">
                {graphWait ? "Construction du graphe..." : "Transcription en cours..."}
              </h3>
              
              <p className="text-[#64748B] text-sm font-normal mt-2 max-w-[280px]">
                {graphWait
                  ? "Les événements extraits des transcriptions sont en cours de mise en relation."
                  : "Vos fichiers audio sont en cours d'analyse pour en extraire les voix et événements historiques."}
              </p>

              {waitingTotalFiles > 0 && (
                <div className="w-full mt-6 px-4 py-3.5 bg-blue-50/50 border border-blue-100/50 rounded-2xl flex flex-col items-center">
                  <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-1.5">
                    Statut du traitement
                  </span>
                  <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
                    {transcriptionWaitQuery.data?.transcriptions.length ?? 0} / {waitingTotalFiles} fichier{waitingTotalFiles > 1 ? "s" : ""} prêt{(transcriptionWaitQuery.data?.transcriptions.length ?? 0) > 1 ? "s" : ""}
                  </span>
                </div>
              )}

              <p className="text-xs text-slate-400 font-normal mt-5 leading-normal">
                La page se mettra à jour automatiquement. Ne fermez pas cette fenêtre.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
